import * as React from "react";

import Badge from "~/components/shared/Badge";
import Icon from "~/components/shared/Icon";
import Spinner from "~/components/shared/Spinner";
import { getJson } from "~/lib/http";

import FormInputHelp from "~/components/shared/FormInputHelp";
import FormInputLabel from "~/components/shared/FormInputLabel";

import * as Select from "@radix-ui/react-select";
import FormInputErrors from "~/components/shared/FormInputErrors";

// Should mirror the `SCM::Service::ProviderType` list
// in `/app/models/scm/service.rb`

enum SCMProviderType {
  Git = "git",
  GithubApp = "github_app",
  GithubCodeAccessApp = "github_code_access_app",
  GithubEnterpriseApp = "github_enterprise_app",
}

enum RepositoryURLType {
  SSH = "ssh_url",
  HTTPS = "clone_url",
}

type SCMServiceInfo = {
  uuid: string;
  repositories_url: string;
  provider_type: SCMProviderType;
  account_login: string;
  has_code_access: boolean;
};

type RepositoryInfo = {
  full_name: string;
  description: string;
  private: boolean;
  fork: boolean;
  language: string | null | undefined;
  html_url: string;
  ssh_url: string;
  clone_url: string;
  default_branch: string;
};

type ElementAttributes = {
  id?: string;
  name?: string;
  value?: string;
  required?: boolean;
  // The auto focus attributes are a bit weird. This type is used for typing attributes
  // from both HTML elements and React DOM elements, which have different casings, so we need to
  // support both for cross-compatibility
  autoFocus?: boolean; // React DOM casing
  autofocus?: boolean; // HTML casing
};

type PropsFromFormElements = {
  label: string | null | undefined;
  hint?: React.ReactNode;
  repositoryFieldAttrs: ElementAttributes;
  errors?: string[];
};

type Props = {
  errors?: string[];
  connectAccountURL: string;
  connectGitHubAccountURL: string;
  services: Array<SCMServiceInfo> | null | undefined;
  providerAccountLogin: string | null | undefined;
  providerRepository: RepositoryInfo | null | undefined;
  allowWebhooksCheckbox: boolean | null | undefined;
  onRepositoryChange?: (repository: {
    name: string;
    description?: string;
    defaultBranch?: string;
  }) => void;
} & PropsFromFormElements;

type RepositoriesData = Array<RepositoryInfo>;

type State = {
  showFilterField: boolean;
  selectedServiceUuid?: string | null;
  repositories: RepositoriesData | null | undefined;
  selectedRepository?: RepositoryInfo | null;
  selectedRepositoryURLType: RepositoryURLType;
  suggestionsVisible: boolean;
  search: string | null | undefined;
  loading: boolean;
  loadingFailed: boolean;
  hasUserSelectedAccount: boolean;
};

const IGNORED_HTML_ATTRIBUTES = ["class", "type", "style"];

const getAttributeListFromElement = (
  element?: HTMLElement | null,
): ElementAttributes | null | undefined => {
  if (!element) {
    return;
  }

  // Filter the element's attributes, removing those we want to ignore
  const copyAttributeList = Array.from(element.attributes).filter(
    (attribute) => IGNORED_HTML_ATTRIBUTES.indexOf(attribute.name) === -1,
  );

  // Transform the list of attributes into an ElementAttributes object
  return copyAttributeList.reduce<Partial<ElementAttributes>>((propsObject, { name, value }) => {
    propsObject[name] = value;
    return propsObject;
  }, {});
};

const SCM_PROVIDER_TYPE_ICON_MAP: Record<SCMProviderType, string> = {
  [SCMProviderType.Git]: "git",
  [SCMProviderType.GithubApp]: "github",
  [SCMProviderType.GithubCodeAccessApp]: "github",
  [SCMProviderType.GithubEnterpriseApp]: "github", // Added to support Restricted GitHub app
};

const NO_ACCOUNT = "none";

const AccountIcon = ({ type }: { type?: SCMProviderType }) => {
  if (!type) {
    return null;
  }

  return (
    <Icon
      icon={SCM_PROVIDER_TYPE_ICON_MAP[type]}
      className="block h-4 w-4"
      style={{ color: "#555555" }}
    />
  );
};

const AccountSelect = (props: {
  accounts?: SCMServiceInfo[] | null;
  selected?: SCMServiceInfo | null;
  onChange: (uuid: string | null) => void;
  connectAccountURL: string;
  connectGitHubAccountURL: string;
}) => {
  const { accounts, selected, onChange } = props;

  if (!accounts) {
    return null;
  }

  const handleChange = React.useCallback(
    (value: string) => {
      onChange(value === NO_ACCOUNT ? null : value);
    },
    [onChange],
  );

  return (
    <Select.Root
      onValueChange={handleChange}
      value={selected ? selected.uuid : NO_ACCOUNT}
      defaultValue={NO_ACCOUNT}
    >
      <Select.Trigger
        className="shadow-input flex items-center rounded-sm border border-[#ccc] bg-white px-[.86em] py-[0.33em] pr-[.33em]"
        data-testid="account-select"
      >
        <Select.Value placeholder="Select account">
          <span className="flex items-center gap-2">
            {selected?.provider_type ? (
              <AccountIcon type={selected?.provider_type} />
            ) : (
              <Icon icon="git" className="h-4 w-4" />
            )}
            <span>{selected?.account_login || "Any account"}</span>
          </span>
        </Select.Value>
        <Select.Icon>
          <Icon icon="custom/outline/chevron-down" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content>
          <Select.ScrollUpButton />
          <Select.Viewport className="shadow-depth-150 rounded-sm bg-white">
            <Select.Group className="p-1">
              <Select.Item
                value={NO_ACCOUNT}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 hover:bg-purple-100 hover:text-purple-600 hover:outline-none aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600"
              >
                <AccountIcon type={SCMProviderType.Git} />
                <span className="flex-1">
                  <Select.ItemText>Any account</Select.ItemText>
                </span>
                <Select.ItemIndicator>
                  <Icon
                    aria-hidden="true"
                    icon="heroicons/16/solid/check"
                    className="block h-4 w-4"
                  />
                </Select.ItemIndicator>
              </Select.Item>

              {accounts.map((service) => (
                <Select.Item
                  key={service.uuid}
                  value={service.uuid}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1 hover:bg-purple-100 hover:text-purple-600 hover:outline-none aria-[selected=true]:bg-purple-100 aria-[selected=true]:text-purple-600"
                >
                  <AccountIcon type={service.provider_type} />
                  <span className="flex-1">
                    <Select.ItemText>
                      {service.account_login}
                      {service.provider_type === SCMProviderType.GithubApp && " (Limited Access)"}
                    </Select.ItemText>
                  </span>
                  <Select.ItemIndicator>
                    <Icon
                      aria-hidden="true"
                      icon="heroicons/16/solid/check"
                      className="block h-4 w-4"
                    />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Group>

            {props.connectAccountURL && (
              <>
                <Select.Separator className="border-b" />

                <Select.Group className="p-1">
                  {accounts.length === 0 && props.connectGitHubAccountURL && (
                    <a
                      href={props.connectGitHubAccountURL}
                      key="github"
                      className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-inherit hover:bg-purple-100 hover:text-purple-600 active:text-purple-600"
                      // eslint-disable-next-line react/jsx-no-bind
                      onClick={(event) => event.stopPropagation()}
                    >
                      <AccountIcon type={SCMProviderType.GithubCodeAccessApp} />
                      <span>Connect GitHub account</span>
                    </a>
                  )}

                  <a
                    href={props.connectAccountURL}
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-inherit hover:bg-purple-100 hover:text-purple-600 active:text-purple-600"
                    // eslint-disable-next-line react/jsx-no-bind
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Icon
                      icon="heroicons/16/solid/plus"
                      className="block h-4 w-4"
                      aria-hidden="true"
                    />
                    <span>Connect account</span>
                  </a>
                </Select.Group>
              </>
            )}
          </Select.Viewport>
          <Select.ScrollDownButton />
          <Select.Arrow />
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
};

export default class ServiceRepositorySelector extends React.PureComponent<Props, State> {
  static getDerivedPropsFromFormBuilderElements(element: HTMLElement): PropsFromFormElements {
    // Helper method to automatically clone the props from form elements
    // created using a form_builder component.
    //
    // `element` should be an HTML element which houses a repository URL field.
    //
    // This throws TypeErrors if the element doesn't contain the required label
    // and hint fields, as well as at least the repository field.

    const errors: string[] = [];
    const validationError = element.querySelector<HTMLDivElement>(".FormField__Error")?.textContent;
    if (validationError) {
      errors.push(validationError);
    }

    const labelElement = element.querySelector<HTMLElement>(".FormField__Label");
    if (!labelElement) {
      throw new TypeError(
        "[ServiceRepositorySelector] Could not get props for: 'FormField__Label' element is missing",
      );
    }

    const hintElement = element.querySelector<HTMLElement>(".FormField__Hint");
    if (!hintElement) {
      throw new TypeError(
        "[ServiceRepositorySelector] Could not get props for: 'FormField__Hint' element is missing",
      );
    }

    const repositoryFieldAttrs = getAttributeListFromElement(
      element.querySelector<HTMLInputElement>('input[name*="[repository]"]'),
    );
    if (!repositoryFieldAttrs) {
      throw new TypeError("[ServiceRepositorySelector] Could not get 'repository' field props");
    }
    // This is a bit of a hack to handle the fact that the form_builder generates HTML
    // elements with HTML attribute name casing, but React expects camelCased props.
    if (repositoryFieldAttrs?.autofocus !== undefined) {
      repositoryFieldAttrs.autoFocus = repositoryFieldAttrs.autofocus;
      delete repositoryFieldAttrs.autofocus;
    }

    return {
      errors,
      label: labelElement.innerText,
      hint: hintElement.innerText,
      repositoryFieldAttrs,
    };
  }

  repositorySearchTimeout: number | null | undefined;
  selectorInputRef = React.createRef<HTMLInputElement>();
  selectorWrapperRef = React.createRef<HTMLDivElement>();

  state: State = {
    showFilterField: true,
    selectedServiceUuid: null,
    repositories: null,
    selectedRepository: null,
    selectedRepositoryURLType: RepositoryURLType.SSH,
    suggestionsVisible: false,
    search: null,
    loading: false,
    loadingFailed: false,
    hasUserSelectedAccount: false,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    // If we don't have a list of services, we won't even show the selector.
    // Calling code should probably instead not mount the component,
    // but otherwise this is a safe fallback.
    if (!props.services) {
      return null;
    }

    // If the selectedServiceUuid already exists, we don't want to re-set it
    if (state.selectedServiceUuid) {
      return null;
    }

    // If the user has explicitly selected an account (including "Any account"),
    // don't override their choice with auto-selection logic
    if (state.hasUserSelectedAccount) {
      return null;
    }

    let selectedService: SCMServiceInfo | undefined;

    // Select the first account if there's only one to select from.
    if (props.services.length === 1) {
      selectedService = props.services[0];
    } else {
      // Okay, if we have services, find the one that matches our provided
      // account_login, if there is one, so we can show the dropdown with it.
      selectedService = props.services.find(
        (service) => service.account_login === props.providerAccountLogin,
      );
    }

    // The current repository URL for the Pipeline
    const pipelineRepositoryURL = props.repositoryFieldAttrs.value;

    // providerRepository is repository metadata provided by the backend (sourced from the GitHub API).  It includes
    // the canonical URLs for GIT (ssh_url) and HTTPS (clone_url).
    //
    // The backend determines the repository from the pipelineRepositoryURL, but it uses relaxed matching that isn't
    // strict about details like the `.git` suffix on the URL. This means providerRepository might be set even if
    // pipelineRepositoryURL doesn't match any of the repository's canonical URLs.
    const providerRepository = props.providerRepository;

    // Check if our pipelineRepositoryURL matches a canonical URL for providerRepository.
    let pipelineRepositoryURLType: RepositoryURLType | undefined;
    if (providerRepository) {
      pipelineRepositoryURLType = Object.values(RepositoryURLType).find(
        (type) => providerRepository[type] === pipelineRepositoryURL,
      );
    }
    // When a repository is selected, we render a disabled URL field with the canonical URL for the
    // selectedRepositoryURLType. We only do this if we found an exact match, as we don't want to show a different URL
    // that misrepresents the current configuration.
    const selectedRepository = pipelineRepositoryURLType ? providerRepository : null;

    return {
      selectedServiceUuid: selectedService && selectedService.uuid,
      selectedRepository,
      selectedRepositoryURLType: pipelineRepositoryURLType || state.selectedRepositoryURLType,
      // Hide the filter field if we've got a repository selected
      showFilterField: !selectedRepository,
    };
  }

  get selectedService() {
    if (!this.state.selectedServiceUuid) {
      return null;
    }

    if (!this.props.services) {
      return null;
    }

    return this.props.services.find((service) => service.uuid === this.state.selectedServiceUuid);
  }

  componentDidMount() {
    // Fetch the repositories, but only if we're showing the filter field
    if (this.state.showFilterField) {
      this.fetchRepositories();
    }

    // If we have a selected repository on mount (from props), call onRepositoryChange
    // to populate the name field with the repository name
    if (this.state.selectedRepository && this.props.onRepositoryChange) {
      this.props.onRepositoryChange({
        name: this.state.selectedRepository.full_name.split("/").pop() || "",
        description: this.state.selectedRepository.description,
        defaultBranch: this.state.selectedRepository.default_branch,
      });
    }
  }

  handleGlobalFocus = (event: Event) => {
    // If suggestions already aren't visible, do nothing
    // This state shouldn't be possible, but just in case!
    if (!this.state.suggestionsVisible) {
      return;
    }

    const currentRef = this.selectorWrapperRef.current;
    const target = event.target;

    // If suggestions _are_ visible, we lack a currently-rendered wrapper, the
    // target is a Node, not an HTML Element, or the new focused element is not
    // within said wrapper, hide the suggestions list as it's no longer needed.
    if (!currentRef || !(target instanceof HTMLElement) || !currentRef.contains(target)) {
      this.setState({ suggestionsVisible: false });
    }
  };

  handleWindowKeydown = (event: KeyboardEvent) => {
    event = event || window.event;

    // If the user hit escape, give the focus back to the input element
    if (event.key === "Escape") {
      event.preventDefault();
      this.selectorInputRef.current && this.selectorInputRef.current.focus();
      this.setState({ suggestionsVisible: false });
      return;
    }

    const activeElement = document.activeElement;
    const linkClass = `${this.constructor.name}-link`;

    if (
      activeElement &&
      activeElement.tagName === "INPUT" &&
      (event.key === "ArrowDown" || event.key === "Enter")
    ) {
      event.preventDefault();
      const firstLink =
        this.selectorWrapperRef.current &&
        this.selectorWrapperRef.current.querySelector<HTMLElement>(linkClass);

      if (firstLink) {
        firstLink.focus();
      }
    }

    if (activeElement && activeElement.classList.contains(linkClass) && event.key === "ArrowDown") {
      event.preventDefault();
      // Walk up the DOM one element, as we're inside list items, then to another item
      const currentListItem = activeElement.parentElement;
      const nextElementSibling = currentListItem && currentListItem.nextElementSibling;
      const nextLink = nextElementSibling && nextElementSibling.firstElementChild;
      if (nextLink && nextLink.classList.contains(linkClass)) {
        // get around Flow's typing, which asserts that nextElementSibling returns an object of type Element, not HTMLElement
        const htmlLink = nextLink as HTMLElement;
        htmlLink.focus();
      }
    }

    if (activeElement && activeElement.classList.contains(linkClass) && event.key === "ArrowUp") {
      event.preventDefault();
      // Walk up the DOM one element, as we're inside list items, then to another item
      const currentListItem = activeElement.parentElement;
      const previousElementSibling = currentListItem && currentListItem.previousElementSibling;
      const prevLink = previousElementSibling && previousElementSibling.firstElementChild;
      if (prevLink && prevLink.classList.contains(linkClass)) {
        // get around Flow's typing, which asserts that previousElementSibling returns an object of type Element, not HTMLElement
        const htmlLink = prevLink as HTMLElement;
        htmlLink.focus();
      } else {
        // If you press up and there is no link above, give focus to the input
        this.selectorInputRef.current && this.selectorInputRef.current.focus();
      }
    }
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Check if whether we should display suggestions has changed,
    // and handle listeners for the dropdown appropriately
    if (this.shouldDisplaySuggestionsForState(prevState) !== this.shouldDisplaySuggestions) {
      if (this.shouldDisplaySuggestions) {
        // Handle keydown events to navigate the dropdown
        window.addEventListener("keydown", this.handleWindowKeydown);

        // Listen for blur events (e.g. clicking outside the dropdown, tabbing away etc.)
        document.addEventListener("mousedown", this.handleGlobalFocus);
        document.addEventListener("focus", this.handleGlobalFocus, true);
      } else {
        // Stop listening for blur events
        document.removeEventListener("focus", this.handleGlobalFocus, true);
        document.removeEventListener("mousedown", this.handleGlobalFocus);
        window.removeEventListener("keydown", this.handleWindowKeydown);
      }
    }

    // If we just showed the filter field, and the repository list isn't ready
    if (this.state.showFilterField && !prevState.showFilterField && !this.state.repositories) {
      // Immediately clear the timeout to fetch repositories
      if (this.repositorySearchTimeout) {
        clearTimeout(this.repositorySearchTimeout);
      }

      // And immediately go update the repository list
      this.fetchRepositories();
      return;
    }

    if (this.state.search !== prevState.search) {
      // If a timeout is already present,
      // clear it since the user is still typing
      if (this.repositorySearchTimeout) {
        clearTimeout(this.repositorySearchTimeout);
      }

      // Instead of doing a search on each keypress,
      // do it a few ms after they've stopped typing
      this.repositorySearchTimeout = window.setTimeout(() => {
        this.fetchRepositories();
        delete this.repositorySearchTimeout;
      }, 250);
    }
  }

  fetchRepositories = () => {
    if (window.Features.SkipNewPipelinesRepositoriesAutocomplete) {
      return;
    }

    const service = this.selectedService;

    if (!service) {
      return;
    }

    let url = service.repositories_url;
    const searchString = this.state.search;

    if (searchString) {
      url += "?search=" + encodeURIComponent(searchString);
    }

    this.setState({ loading: true, loadingFailed: false }, () => {
      getJson<RepositoriesData>(url)
        .then((repositories) => {
          // Multiple requests may be in flight at a time and we only care about the
          // response for the latest request.
          // This comparison checks the latest search string and selected service against
          // those that were used to make this request before updating state with the
          // request's response
          if (
            searchString === this.state.search &&
            service.repositories_url === this.selectedService?.repositories_url
          ) {
            this.setState({
              repositories,
              loading: false,
            });
          }
        })
        .catch(() => {
          if (
            searchString === this.state.search &&
            service.repositories_url === this.selectedService?.repositories_url
          ) {
            this.setState({
              repositories: null,
              loading: false,
              loadingFailed: true,
            });
          }
        });
    });
  };

  renderRepository(repository: RepositoryInfo) {
    return (
      <>
        <h5 className="my-1">
          {repository.private && (
            <>
              <Icon icon="eye-strikethrough" title="Private" />{" "}
            </>
          )}
          {repository.full_name}
          {repository.language && (
            <Badge className="mx-1" outline={true} bold={true}>
              {repository.language}
            </Badge>
          )}
          {repository.fork && (
            <>
              {" "}
              <Icon icon="code" title="Fork" />
            </>
          )}
        </h5>
        <p className="my-1 max-w-full truncate" title={repository.description}>
          {repository.description || "No description"}
        </p>
      </>
    );
  }

  handleRepositorySelect = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const currentTarget = event.currentTarget as HTMLAnchorElement;

    // Weird, that shouldn't happen!
    if (!currentTarget.dataset.repositoryIndex) {
      return;
    }

    const selectedRepositoryIndex = parseInt(currentTarget.dataset.repositoryIndex, 10);

    if (selectedRepositoryIndex === null || selectedRepositoryIndex === undefined) {
      return;
    }

    const selectedRepository =
      this.state.repositories && this.state.repositories[selectedRepositoryIndex];

    if (!selectedRepository) {
      return;
    }

    event.preventDefault();

    this.props.onRepositoryChange?.({
      name: selectedRepository.full_name.split("/").pop() || "",
      description: selectedRepository.description,
      defaultBranch: selectedRepository.default_branch,
    });

    this.setState({
      selectedRepository,
      showFilterField: false,
      suggestionsVisible: false,
    });
  };

  renderRepositories(): React.ReactNode {
    if (!this.state.repositories) {
      return <Spinner />;
    }

    return this.state.repositories.map((repository, index) => (
      <li key={repository.html_url}>
        <a
          href="#"
          className={`${this.constructor.name}-link focus-bg-lime black focus-white block px-2 py-1 no-underline`}
          style={{
            outline: "none",
          }}
          data-repository-index={index}
          onClick={this.handleRepositorySelect}
        >
          {this.renderRepository(repository)}
        </a>
      </li>
    ));
  }

  handleServiceChange = (uuid: string | null) => {
    if (!uuid) {
      this.setState({
        repositories: null,
        selectedServiceUuid: null,
        hasUserSelectedAccount: true,
      });
    } else {
      this.setState({ selectedServiceUuid: uuid, hasUserSelectedAccount: true }, () => {
        this.fetchRepositories();
      });
    }
  };

  get shouldDisplaySuggestions() {
    return this.shouldDisplaySuggestionsForState(this.state);
  }

  shouldDisplaySuggestionsForState(state: State) {
    return state.repositories && state.repositories.length > 0 && state.suggestionsVisible;
  }

  renderRepositorySuggestions() {
    const style = {
      display: this.shouldDisplaySuggestions ? "block" : "none",
      marginTop: 3,
      zIndex: 999,
      width: "100%",
      lineHeight: 1.4,
      maxHeight: 320,
    } as const;

    return (
      <div
        className="border-silver absolute overflow-auto rounded-sm border bg-white shadow-sm"
        style={style}
      >
        <ul>{this.renderRepositories()}</ul>
      </div>
    );
  }

  handleRepositoryURLTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedRepositoryURLType = event.target.value as RepositoryURLType;

    if (!Object.values(RepositoryURLType).includes(selectedRepositoryURLType)) {
      throw new Error(`Unexpected repository URL type: ${selectedRepositoryURLType}`);
    }

    this.setState({ selectedRepositoryURLType });
  };

  handleRepositoryFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: event.target.value });
  };

  handleRepositoryFieldFocus = () => {
    // Always mark the suggestions as visible when focused;
    // the dropdown actually relies on the shouldDisplaySuggestions
    // computed value, which means this can account for focus events
    // received while data was still loading.
    this.setState({ suggestionsVisible: true });
  };

  handleChangeRepositoryClick = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    event.preventDefault();

    // Switch back to the filter field and clear the current selection
    this.setState({ showFilterField: true, selectedRepository: null }, () => {
      // Focus the repository URL input so users can immediately type/select again
      if (this.selectorInputRef.current) {
        this.selectorInputRef.current.focus();
      }
    });

    // Clear any autofilled fields (name, description, branch) in the host form
    this.props.onRepositoryChange?.({
      name: "",
      description: "",
      defaultBranch: "",
    });
  };

  render() {
    const {
      errors,
      repositoryFieldAttrs: { value: repositoryFieldValue, ...repositoryFieldAttrs },
    } = this.props;
    let { label, hint } = this.props;

    if (this.props.services) {
      if (label) {
        // If we're on a "Repository URL" field, chop that part off for clarity
        const URLSuffix = " URL";
        const URLIndex = label.indexOf(URLSuffix);
        if (URLIndex !== -1 && URLIndex === label.length - URLSuffix.length) {
          label = label.slice(0, URLIndex);
        }
      }

      if (hint) {
        // Add some info to the hint if we've got a service selected,
        // as it adds some magic to the field
        if (
          this.selectedService &&
          this.state.showFilterField &&
          !window.Features.SkipNewPipelinesRepositoriesAutocomplete
        ) {
          hint = `${hint} Paste a checkout URL, or type to find a repository in the selected account.`;

          // And if loading failed, show a message about that;
          // it's kinda long but we don't have much of a better place
          if (this.state.loadingFailed) {
            hint = `${hint} Failed to load list of repositories. Try again later.`;
          }
        }

        if (!this.state.showFilterField) {
          hint = (
            <span>
              {hint}
              {" Need to "}
              <a
                href="#"
                className="lime hover-lime hover-underline no-underline"
                onClick={this.handleChangeRepositoryClick}
              >
                choose another repository or URL
              </a>
              {/*
               */}
              ?
            </span>
          );
        }
      }
    }

    const content = this.state.showFilterField ? (
      <div className="flex flex-wrap items-center gap-2">
        <AccountSelect
          onChange={this.handleServiceChange}
          accounts={this.props.services}
          selected={this.selectedService}
          connectAccountURL={this.props.connectAccountURL}
          connectGitHubAccountURL={this.props.connectGitHubAccountURL}
        />
        <div
          className="relative min-w-0 flex-auto py-1"
          style={{
            flexShrink: 1,
            flexGrow: 6,
            flexBasis: 250,
          }}
          ref={this.selectorWrapperRef}
        >
          <div className="flex items-center">
            <input
              type="text"
              className="input"
              ref={this.selectorInputRef}
              {...repositoryFieldAttrs}
              defaultValue={
                this.state.selectedRepository
                  ? this.state.selectedRepository[this.state.selectedRepositoryURLType]
                  : repositoryFieldValue
              }
              id={repositoryFieldAttrs.id}
              autoComplete={this.props.services ? "off" : "url"}
              onChange={this.handleRepositoryFieldChange}
              onFocus={this.handleRepositoryFieldFocus}
            />
            {this.state.loading && (
              <span className="pointer-events-none absolute right-2">
                <Spinner color={false} />
              </span>
            )}
          </div>
          {this.renderRepositorySuggestions()}
        </div>
      </div>
    ) : (
      <>
        <div className="border-gray mb-3 flex items-center border px-4 py-2">
          {this.selectedService && (
            <Icon
              icon={SCM_PROVIDER_TYPE_ICON_MAP[this.selectedService.provider_type]}
              className="my-1 mr-3 flex-none"
            />
          )}
          <div className="min-w-0 flex-auto">
            {this.state.selectedRepository && this.renderRepository(this.state.selectedRepository)}
          </div>
          <button
            className="unstyled-button hover-lime hover-bg-white"
            onClick={this.handleChangeRepositoryClick}
            title="Change repository"
          >
            <Icon
              icon="close"
              style={{
                width: 30,
                height: 30,
              }}
            />
          </button>
        </div>
        <div className="flex flex-wrap items-center">
          <label className="mr-1 inline-block flex-none py-1">{"Checkout using: "}</label>
          {Object.values(RepositoryURLType).map((typeIdentifier) => (
            <label key={typeIdentifier} className="relative mr-1 flex items-center py-1 pl-5">
              <input
                // The important flags on these classes are to override legacy styles in
                // the `legacy-vendor-stylesheet` that have element level specificity
                className="radio absolute !-ml-5 !mt-0"
                type="radio"
                checked={this.state.selectedRepositoryURLType === typeIdentifier}
                value={typeIdentifier}
                onChange={this.handleRepositoryURLTypeChange}
              />
              {typeIdentifier === RepositoryURLType.SSH && "SSH"}
              {typeIdentifier === RepositoryURLType.HTTPS && "HTTPS"}
            </label>
          ))}
          <input
            type="text"
            className="input min-w-0 flex-auto"
            style={{ flexBasis: 250 }}
            disabled={true}
            value={
              this.state.selectedRepository
                ? this.state.selectedRepository[this.state.selectedRepositoryURLType]
                : undefined
            }
          />
        </div>
        <input
          type="hidden"
          {...repositoryFieldAttrs}
          value={
            this.state.selectedRepository
              ? this.state.selectedRepository[this.state.selectedRepositoryURLType]
              : undefined
          }
          id={repositoryFieldAttrs.id}
        />
      </>
    );

    let webhooks;
    if (
      this.props.allowWebhooksCheckbox &&
      ((window.Features.SkipNewPipelinesRepositoriesAutocomplete &&
        this.state.selectedServiceUuid) ||
        !this.state.showFilterField)
    ) {
      webhooks = (
        <>
          <hr
            className="bg-gray p-0"
            style={{
              border: "none",
              height: "1px",
              marginLeft: "-15px",
              marginRight: "-15px",
            }}
          />

          <div className="form-group">
            <input
              name="auto_create_webhooks"
              id="auto_create_webhooks"
              type="checkbox"
              value="true"
              defaultChecked={true}
            />
            <label className="FormField__Label pl-1" htmlFor="auto_create_webhooks">
              Auto-create webhooks
            </label>
            <p className="FormField__Hint">
              Webhooks are used to trigger builds in Buildkite. When selected, we&apos;ll attempt to
              create them automatically.
            </p>
          </div>
        </>
      );
    }

    return (
      <>
        <FormInputLabel
          label={label}
          required={!!repositoryFieldAttrs.required}
          htmlFor={repositoryFieldAttrs.id}
          errors={errors && errors?.length > 0}
        />
        {content}
        {errors?.length ? <FormInputErrors errors={errors} /> : null}
        <FormInputHelp>{hint}</FormInputHelp>
        {webhooks}
      </>
    );
  }
}
